import { FC, useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { api } from '../services/api';
import { ScrollArea } from './ui/scroll-area';
import { COPY } from '../lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import clsx from 'clsx';

interface Notification {
    id: string;
    type: string;
    actor_name: string;
    actor_icon: string;
    related_id: string;
    is_read: number;
    created_at: number;
    message?: string; // Derived in API query? No, removed from DB. Logic will generate text.
}

export const NotificationBell: FC = () => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const { playSound } = useGamenics();

    const unreadCount = notifications.filter(n => n.is_read === 0).length;

    const fetchNotifications = async () => {
        const result = await api.getNotifications();
        if (result.success && result.data) {
            setNotifications(result.data);
        }
    };

    useEffect(() => {
        // Fetch initially
        fetchNotifications();
        // Poll every 60s
        const interval = setInterval(fetchNotifications, 60000);
        return () => clearInterval(interval);
    }, []);

    const handleMarkRead = async () => {
        // Optimistic update
        setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
        await api.markNotificationsRead();
        fetchNotifications(); // Refresh to be sure
    };

    const getMessage = (n: Notification) => {
        if (n.message) return n.message;
        if (n.type === 'LIKE') return `があなたの声を手元に残しました！`;
        if (n.type === 'REPLY') return `が同じ想いを返してくれました。`; // Future
        return 'があなたに響き合いました。';
    };

    return (
        <Popover open={isOpen} onOpenChange={(open) => { setIsOpen(open); if (open) fetchNotifications(); }}>
            <PopoverTrigger asChild>
                <button
                    onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); }}
                    className="relative p-2 rounded-full hover:bg-cream-2 transition-colors focus:outline-none group"
                >
                    <Bell className={clsx("h-5 w-5 transition-colors", unreadCount > 0 ? "text-pop" : "text-ink-2 group-hover:text-ink")} />
                    {unreadCount > 0 && (
                        <>
                            <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-pop animate-pulse border-2 border-surface" />
                            <span className="absolute inset-0 rounded-full border border-pop/50 animate-ping opacity-20" />
                        </>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0 bg-surface border-2 border-ink text-ink rounded-pop-md overflow-hidden" align="end">
                <div className="flex items-center justify-between px-4 py-3 border-b-2 border-cream-2">
                    <h4 className="font-bold text-xs tracking-widest text-ink-2 uppercase">お知らせ</h4>
                    {unreadCount > 0 && (
                        <button
                            className="text-[10px] font-bold text-primary hover:text-pop transition-colors flex items-center gap-1"
                            onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); handleMarkRead(); }}
                        >
                            {COPY.markAllRead}
                        </button>
                    )}
                </div>
                <ScrollArea className="h-[300px]">
                    {notifications.length === 0 ? (
                        <div className="p-8 text-center text-xs text-ink-3 font-display">
                            {COPY.noItems}
                        </div>
                    ) : (
                        <div className="grid">
                            {notifications.map((n) => (
                                <div key={n.id} className={`px-4 py-3 border-b-2 border-cream-2 flex gap-3 items-start hover:bg-cream transition-colors ${n.is_read === 0 ? 'bg-cream' : ''}`}>
                                    <div className="mt-1">
                                        {n.actor_icon ? (
                                            <img src={n.actor_icon} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-ink/15" />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-cream-2 flex items-center justify-center text-xs">?</div>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-sm">
                                            <span className="font-bold text-ink">{n.actor_name || '誰か'}</span>{' '}
                                            <span className="text-ink-2">{getMessage(n)}</span>
                                        </p>
                                        <p className="text-xs text-ink-3">
                                            {new Date(n.created_at * 1000).toLocaleString()}
                                        </p>
                                    </div>
                                    {n.is_read === 0 && (
                                        <div className="mt-2 w-2 h-2 rounded-full bg-primary" />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
}
